import { useCallback, useEffect, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { ChevronDown, ChevronLeft, ExternalLink, Loader2, Pause, Pencil, Play, RefreshCw, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import FdeShell from '@/frontend/features/Fde/FdeShell'
import { Banner, DeliveryChip, ResultChip, SpecBlocks, StatusPill, attentionText } from '@/frontend/features/Fde/triggers/components'
import { AskAgent, PreviewSummary, SimulateButton } from '@/frontend/features/Fde/triggers/agentic'
import type { Evaluation, Preview, Trigger, TriggerEvent, TriggersPageProps } from '@/frontend/features/Fde/triggers/types'
import { ago, entityText, fill, getJson, postJson, when, withProject } from '@/frontend/features/Fde/triggers/types'

interface Props extends TriggersPageProps {
  trigger: Trigger | null
  evaluations: Evaluation[]
  events: TriggerEvent[]
  analysis_update?: { title?: string; missing?: boolean; current_version?: string; pinned_version?: string } | null
}

type Attempt = { attempt: number; outcome: string; http_status: number | null; error: string | null; response_excerpt: string | null; started_at: string }

const REFRESH_MS = 30_000

function Changes({ e }: { e: Evaluation }) {
  if (e.rows_matched == null) return <span className="text-muted-foreground">—</span>
  return <span className="tabular-nums">{e.entered} · {e.changed} · {e.exited}</span>
}

function EvaluationRow({ e, copy }: { e: Evaluation; copy: Record<string, string> }) {
  const [open, setOpen] = useState(false)
  const reason = e.error ?? e.gate?.reasons?.[0] ?? ''
  const connections = e.gate?.connections ?? []
  return (
    <>
      <tr className="border-t border-border align-top">
        <td className="whitespace-nowrap py-2.5 pr-3 text-muted-foreground">{when(e.started_at)}</td>
        <td className="py-2.5 pr-3"><ResultChip result={e.status} copy={copy} /></td>
        <td className="py-2.5 pr-3 tabular-nums">{e.rows_matched ?? '—'}</td>
        <td className="py-2.5 pr-3"><Changes e={e} /></td>
        <td className="py-2.5 pr-3 tabular-nums">{e.events_created}</td>
        <td className="py-2.5 text-xs text-muted-foreground">
          <div className="flex items-start gap-2">
            <span className="min-w-0 flex-1 break-words">{reason}</span>
            {connections.length > 0 && (
              <button type="button" onClick={() => setOpen(!open)} className="shrink-0 rounded p-0.5 hover:bg-accent" aria-expanded={open} aria-label="Dados por conexão">
                <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
              </button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="pb-3">
            <div className="grid gap-1.5 rounded-lg bg-muted/40 p-3 text-xs">
              {connections.map((c) => (
                <div key={c.connection_id} className="flex flex-wrap gap-x-4 gap-y-0.5">
                  <span className="font-medium">{c.name || c.connection_id}</span>
                  <span>sincronização: {c.sync ?? '—'} {c.sync_finished_at ? `(${when(c.sync_finished_at)})` : ''}</span>
                  <span>preparação: {c.preparation ?? '—'} {c.preparation_finished_at ? `(${when(c.preparation_finished_at)})` : ''}</span>
                  {c.in_progress && <span className="text-amber-700">em andamento</span>}
                </div>
              ))}
              {e.watermark && <span className="text-muted-foreground">versão dos dados: <code>{e.watermark.slice(0, 12)}</code></span>}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function EventRow({ event, copy, detailUrl, retryUrl, canManage, onRetried }: {
  event: TriggerEvent; copy: Record<string, string>; detailUrl: string; retryUrl: string; canManage: boolean; onRetried: () => void
}) {
  const [open, setOpen] = useState(false)
  const [attempts, setAttempts] = useState<Attempt[] | null>(null)
  const [busy, setBusy] = useState(false)
  const toggle = async () => {
    setOpen(!open)
    if (!open && attempts == null) {
      const body = await getJson<{ event: { attempts: Attempt[] } }>(detailUrl).catch(() => null)
      setAttempts(body?.event.attempts ?? [])
    }
  }
  const retry = async () => {
    setBusy(true)
    await postJson(retryUrl, {}).catch(() => null)
    setBusy(false)
    onRetried()
  }
  return (
    <>
      <tr className="cursor-pointer border-t border-border align-top hover:bg-accent/30" onClick={toggle}>
        <td className="whitespace-nowrap py-2.5 pr-3 text-muted-foreground">{when(event.created_at)}</td>
        <td className="py-2.5 pr-3">{copy[`kind_${event.kind}`]}</td>
        <td className="max-w-[16rem] truncate py-2.5 pr-3 font-mono text-xs">{entityText(event.entity_key)}</td>
        <td className="py-2.5 pr-3"><DeliveryChip status={event.status} copy={copy} /></td>
        <td className="py-2.5 pr-3 tabular-nums">{event.attempts}</td>
        <td className="py-2.5 text-xs">
          <div className="flex items-center justify-end gap-2" onClick={(click) => click.stopPropagation()}>
            {event.execution_url
              ? <a href={event.execution_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono hover:underline">{event.execution_id ?? 'abrir'}<ExternalLink className="size-3" aria-hidden /></a>
              : <span className="font-mono text-muted-foreground">{event.execution_id ?? ''}</span>}
            {canManage && (event.status === 'dead' || event.status === 'failed') && (
              <Button size="sm" variant="outline" className="h-7" onClick={retry} disabled={busy}>
                {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <RotateCcw className="size-3.5" aria-hidden />}{copy.retry}
              </Button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className="pb-3">
            <div className="grid gap-3 rounded-lg bg-muted/40 p-3 text-xs @3xl:grid-cols-2">
              <div className="grid gap-1">
                <span className="font-medium">{copy.event_data}</span>
                <pre className="max-h-60 overflow-auto rounded bg-background p-2 font-mono text-[11px]">{JSON.stringify(event.payload, null, 2)}</pre>
                {event.last_error && <span className="text-destructive">{event.last_error}</span>}
              </div>
              <div className="grid content-start gap-1">
                <span className="font-medium">{copy.event_attempts}</span>
                {attempts == null ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : attempts.length === 0 ? <span className="text-muted-foreground">—</span> : (
                  <ul className="grid gap-1">
                    {attempts.map((a) => (
                      <li key={a.attempt} className="flex flex-wrap gap-x-3">
                        <span className="tabular-nums">#{a.attempt}</span><span>{when(a.started_at)}</span>
                        <span className="font-medium">{a.outcome}</span>{a.http_status && <span>HTTP {a.http_status}</span>}
                        {a.error && <span className="text-muted-foreground">{a.error}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function TriggerShow(props: Props) {
  const copy = props.triggers_copy
  const projectId = props.project?.id
  const trigger = props.trigger
  const [evaluations, setEvaluations] = useState(props.evaluations)
  const [events, setEvents] = useState(props.events)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(props.error ?? null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const base = trigger ? `${props.paths.base}/${trigger.id}` : ''

  const refresh = useCallback(async () => {
    if (!trigger) return
    const [e, v] = await Promise.all([
      getJson<{ evaluations: Evaluation[] }>(withProject(`${base}/detail`, projectId, { part: 'evaluations', limit: '30' })).catch(() => null),
      getJson<{ events: TriggerEvent[] }>(withProject(`${base}/detail`, projectId, { part: 'events', limit: '50' })).catch(() => null),
    ])
    if (e) setEvaluations(e.evaluations)
    if (v) setEvents(v.events)
  }, [base, projectId, trigger])

  // a reload after an action brings new props: the lists follow them
  useEffect(() => setEvaluations(props.evaluations), [props.evaluations])
  useEffect(() => setEvents(props.events), [props.events])

  useEffect(() => {
    const timer = window.setInterval(refresh, REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [refresh])

  if (!trigger) {
    return (
      <FdeShell copy={props.copy} eyebrow="" title={props.project?.name ?? props.organization.name} description="" projectId={projectId}>
        <div className="mx-auto max-w-3xl p-6"><Banner tone="error">{failure ?? 'Gatilho não encontrado.'}</Banner></div>
      </FdeShell>
    )
  }

  const act = async (key: string, url: string, body: unknown, done?: string) => {
    setBusy(key); setFailure(null); setNotice(null)
    try {
      await postJson(withProject(url, projectId), body)
      if (done) setNotice(done)
      router.reload({ only: ['trigger', 'evaluations', 'events'] })
    } catch (error) {
      setFailure((error as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const simulate = async () => {
    setPreviewing(true); setFailure(null)
    try {
      setPreview(await postJson<Preview>(withProject(`${props.paths.base}/preview`, projectId), { spec: trigger.spec, trigger_id: trigger.id }))
    } catch (error) {
      setFailure((error as Error).message)
    } finally {
      setPreviewing(false)
    }
  }
  const target = trigger.spec.target
  const workflowMissing = target.kind === 'praxis' && props.praxis.names.length > 0 && !props.praxis.names.includes(target.workflow)
  const latest = evaluations[0]
  const dead = events.filter((e) => e.status === 'dead').length
  const attention = attentionText({ ...trigger, last_status: latest?.status, last_gate: latest?.gate, last_error: latest?.error, events_dead: dead }, copy)
  const actions = (
    <Link href={props.paths.index} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
      <ChevronLeft className="size-3.5" aria-hidden />{copy.back}
    </Link>
  )

  return (
    <>
      <Head title={`${copy.title}: ${trigger.name}`} />
      <FdeShell copy={props.copy} eyebrow="" title={props.project?.name ?? props.organization.name} description="" projectId={projectId}>
        <div className="@container h-full overflow-y-auto bg-muted/10">
          <div className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)] gap-6 px-4 pb-16 pt-6 @3xl:px-6">
            {actions}
            <header className="flex flex-wrap items-start justify-between gap-4">
              <div className="grid min-w-0 gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-semibold tracking-tight">{trigger.name}</h2>
                  <StatusPill status={trigger.status} copy={copy} />
                  <span className="text-xs text-muted-foreground">v{trigger.current_revision}</span>
                </div>
                {trigger.description && <p className="max-w-3xl text-sm text-muted-foreground">{trigger.description}</p>}
                <p className="text-xs text-muted-foreground">
                  {copy.last_look} {ago(latest?.finished_at ?? latest?.started_at)}
                  {trigger.status === 'active' && trigger.next_evaluation_at && <> · {copy.next_look} {when(trigger.next_evaluation_at)}</>}
                </p>
              </div>
              {props.can_manage && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="ghost" size="sm" asChild>
                    <Link href={withProject(`${base}/edit`, projectId)}><Pencil className="size-4" aria-hidden />{copy.edit_manual}</Link>
                  </Button>
                  <AskAgent agents={props.trigger_agents} copy={copy} variant="outline" title={copy.ask_fix_title} label={copy.ask_agent_fix}
                    template={fill(copy.ask_fix_template, { name: trigger.name, id: trigger.id })} />
                  {(trigger.status === 'active' || trigger.status === 'suspended') && (
                    <Button variant="outline" size="sm" disabled={busy != null} onClick={() => act('evaluate', `${base}/evaluate`, {}, copy.evaluate_queued)}>
                      {busy === 'evaluate' ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <RefreshCw className="size-4" aria-hidden />}{copy.evaluate_now}
                    </Button>
                  )}
                  {trigger.status === 'active' ? (
                    <Button variant="outline" size="sm" disabled={busy != null} onClick={() => act('status', `${base}/status`, { status: 'paused' })}>
                      <Pause className="size-4" aria-hidden />{copy.pause}
                    </Button>
                  ) : (
                    <Button size="sm" disabled={busy != null} onClick={() => act('status', `${base}/status`, { status: 'active' })}>
                      {busy === 'status' ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Play className="size-4" aria-hidden />}{copy.activate}
                    </Button>
                  )}
                </div>
              )}
            </header>

            {failure && <Banner tone="error">{failure}</Banner>}
            {notice && <Banner tone="info">{notice}</Banner>}
            {attention && <Banner tone={attention.tone}>{attention.text}</Banner>}
            {workflowMissing && target.kind === 'praxis' && <Banner tone="error">{fill(copy.workflow_missing, { name: target.workflow })}</Banner>}
            {props.analysis_update && (
              <Banner tone={props.analysis_update.missing ? 'error' : 'warning'}>
                {fill(props.analysis_update.missing ? copy.analysis_missing_banner : copy.analysis_update_banner, { title: props.analysis_update.title ?? '' })}
              </Banner>
            )}
            {trigger.status === 'draft' && (
              <section className="grid gap-3 rounded-xl border border-sky-500/30 bg-sky-50/60 p-4 dark:bg-sky-950/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm">{fill(copy.draft_banner, { by: trigger.created_by ? fill(copy.draft_by, { who: trigger.created_by }) : '' })}</p>
                  <SimulateButton busy={previewing} copy={copy} onRun={simulate} />
                </div>
                {preview && <PreviewSummary preview={preview} copy={copy} />}
              </section>
            )}

            <SpecBlocks spec={trigger.spec} copy={copy} workflowsPath={props.paths.workflows} />

            <Tabs defaultValue="evaluations" className="grid gap-3">
              <TabsList className="w-fit">
                <TabsTrigger value="evaluations">{copy.tab_evaluations}</TabsTrigger>
                <TabsTrigger value="events">{copy.tab_events}{events.length > 0 && <span className="ml-1.5 tabular-nums text-muted-foreground">{events.length}</span>}</TabsTrigger>
                <TabsTrigger value="versions">{copy.tab_versions}</TabsTrigger>
              </TabsList>
              <TabsContent value="evaluations">
                <section className="overflow-x-auto rounded-xl border border-border bg-background px-4 py-2">
                  {evaluations.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{copy.no_evaluations}</p> : (
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs text-muted-foreground">
                        <tr><th className="py-2 pr-3 font-medium">{copy.col_when}</th><th className="py-2 pr-3 font-medium">{copy.col_result}</th>
                          <th className="py-2 pr-3 font-medium">{copy.col_rows}</th><th className="py-2 pr-3 font-medium">{copy.col_changes}</th>
                          <th className="py-2 pr-3 font-medium">{copy.col_events}</th><th className="py-2 font-medium">{copy.col_reason}</th></tr>
                      </thead>
                      <tbody>{evaluations.map((e) => <EvaluationRow key={e.id} e={e} copy={copy} />)}</tbody>
                    </table>
                  )}
                </section>
              </TabsContent>
              <TabsContent value="events">
                <section className="overflow-x-auto rounded-xl border border-border bg-background px-4 py-2">
                  {events.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">{copy.no_events}</p> : (
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs text-muted-foreground">
                        <tr><th className="py-2 pr-3 font-medium">{copy.col_when}</th><th className="py-2 pr-3 font-medium">{copy.col_kind}</th>
                          <th className="py-2 pr-3 font-medium">{copy.col_entity}</th><th className="py-2 pr-3 font-medium">{copy.col_delivery}</th>
                          <th className="py-2 pr-3 font-medium">{copy.col_attempts}</th><th className="py-2 text-right font-medium">{copy.col_execution}</th></tr>
                      </thead>
                      <tbody>
                        {events.map((event) => (
                          <EventRow key={event.id} event={event} copy={copy} canManage={props.can_manage} onRetried={refresh}
                            detailUrl={withProject(`${base}/detail`, projectId, { part: 'event', event_id: event.id })}
                            retryUrl={withProject(`${base}/events/${event.id}/retry`, projectId)} />
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              </TabsContent>
              <TabsContent value="versions">
                <ol className="grid gap-2">
                  {(trigger.revisions ?? []).map((r) => (
                    <li key={r.revision} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-xl border border-border bg-background px-4 py-3 text-sm">
                      <span className="font-medium tabular-nums">v{r.revision}</span>
                      <span>{r.note || '—'}</span>
                      <span className="text-xs text-muted-foreground">{fill(copy.version_by, { who: r.created_by || '—' })} · {when(r.created_at)}</span>
                    </li>
                  ))}
                </ol>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </FdeShell>
    </>
  )
}

TriggerShow.layout = (page: React.ReactElement) => <DashboardLayout variant="fullscreen">{page}</DashboardLayout>
export default TriggerShow
